import { LonaWebComponent, html } from "@tento-ui/component";
import { component } from "@tento-ui/component-decorators";
import { Style, css } from "@tento-ui/component-styles";
import { GESTURE_MANAGER } from "@tento-ui/gesture-manager";

@component({
  name: "lona-pomodoro-notch",
})
export class PomodoroNotch extends LonaWebComponent {
  private $root: HTMLElement = this.hydrate("root");
  private $playPauseBtn: HTMLButtonElement = this.hydrate("play-pause");
  private onPlayPauseClick?: () => void;
  private onNotchClick?: () => void;

  constructor() {
    super();
    // Clicking the notch body opens the timer window. The play/pause button
    // stops pointer propagation (see GESTURE_MANAGER), so its clicks never
    // reach this body handler and only toggle the timer.
    GESTURE_MANAGER.addPointerEvent(this.$root, {
      onClick: () => this.onNotchClick?.(),
    });
    GESTURE_MANAGER.addPointerEvent(this.$playPauseBtn, {
      onClick: () => this.onPlayPauseClick?.(),
    });
  }

  bind(props: PomodoroNotch.Props): this {
    this.$("time").textContent = props.time;
    this.$("label").textContent = props.label;
    this.style.setProperty("--notch-color", props.color);

    this.$playPauseBtn.title = props.state === "running" ? "Pause" : "Resume";
    this.$("icon").textContent = props.state === "running" ? "⏸" : "▶";
    this.$("icon").toggleAttribute("playing", props.state === "running");

    this.onPlayPauseClick = props.onPlayPauseClick;
    this.onNotchClick = props.onNotchClick;
    return this;
  }


  static $styles: Style.Compat[] = [
    css`
      :host([fixed]) {
        position: fixed;
        top: 0px;
        left: 50%;
        transform: translate(-50%, -40px);
        z-index: 8000; /* ZIndex.BLOCKING */
        transition: transform 0.3s ease;
      }

      :host([fixed][revealed]) {
        transform: translate(-50%, 0px);
      }

      :host {
        width: max-content;
      }

      :host([visible]) {
        top: 0;
      }

      :host(:hover) {
        filter: brightness(1.1);
      }

      #root {
        display: flex;
        align-items: center;
        gap: 8px;
        background: var(--notch-color, var(--red));
        color: var(--text-pri-inv);
        padding: 8px 20px;
        border-radius: 0 0 12px 12px;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
        cursor: pointer;
        user-select: none;
        width: max-content;
      }

      #time {
        font-family: monospace;
        font-size: 16px;
        font-weight: bold;
      }

      .control-btn {
        background: none;
        border: none;
        color: white;
        cursor: pointer;
        padding: 0 4px;
        margin: 0;
      }

      .control-btn:hover {
        opacity: 1;
      }

      #container {
        height: 16px;
        width: 16px;
        font-size: 1.25rem;
      }

      #icon[playing] {
        --font-size: 0.875rem;
      }

      #icon {
        --font-size: 0.6125rem;
      }
    `,
  ];

  static $html: Option<HTMLTemplateElement> = html`
    <std-row id="root">
      <std-center id=container>
        <button id="play-pause" class="control-btn">
          <p id=icon>▶</p>
        </button>
      </std-center>
      <p id="time">00:00</p>
      <p id="label">Timer</p>
    </std-row>
  `;
}

export namespace PomodoroNotch {
  export type Props = {
    time: string;
    label: string;
    color: string;
    state: "running" | "paused";
    onPlayPauseClick?: () => void;
    onNotchClick?: () => void;
  };
}
